import {
  Box,
  Checkbox,
  debounce,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Typography,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import SearchInput from "../../../common/components/functional/searchInput/SearchInput";
import useIsLoading from "../../../hooks/useIsLoading";
import { LongOrderManualTalentSearchResult } from "../TalentsStatusAPI";
import {
  searchOtherTalents,
  selectAllPickedCandidates,
} from "../talentsStatusSlice";
import { TalentStatusDisplayName } from "../editOrder/long/talentsStatus/TalentStatusDisplayName";
import { getName, sortByName } from "../../../common/helpers/common";

type Props = {
  onChange: (
    talent: LongOrderManualTalentSearchResult,
    checked: boolean,
  ) => void;
  newSelectedTalents: LongOrderManualTalentSearchResult[];
  bundleId: string;
};

const SearchForOtherTalentsContent = ({
  onChange,
  newSelectedTalents,
  bundleId,
}: Props) => {
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<
    LongOrderManualTalentSearchResult[]
  >([]);
  const [searchCompleted, setSearchCompleted] = useState(false);

  const isLoading = useIsLoading(["talentsStatus"]);
  const pickedCandidates = useAppSelector(selectAllPickedCandidates);

  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (search) {
      const sanitizedSearch = search.trim().replace(/["]/g, ""); // remove double quotes which breaks the backend

      if (sanitizedSearch) {
        dispatch(
          searchOtherTalents({
            bundleId,
            search: sanitizedSearch,
          }),
        ).then((action) => {
          if (isFulfilled(action)) {
            const sortedResults = sortByName(action.payload);
            setSearchResults(sortedResults);
            setSearchCompleted(true);
          }
        });
      }
    } else {
      setSearchResults([]);
    }
  }, [search, bundleId, dispatch, pickedCandidates]);

  const handleSelect = (talent: LongOrderManualTalentSearchResult) => {
    const isExisting = newSelectedTalents.includes(talent);

    if (isExisting) {
      onChange(talent, false);
    } else {
      onChange(talent, true);
    }
  };

  const onInputChange = debounce((newValue: string, reason: string) => {
    if (reason === "input") {
      setSearch(newValue);
      setSearchCompleted(false);
    }
  }, 800);

  const showSearchResults =
    !!searchResults.length && searchCompleted && !isLoading;
  const showNoSearchResults =
    !searchResults.length && searchCompleted && !isLoading;

  return (
    <Box>
      <SearchInput
        value={search}
        onInputChange={(newValue: string, reason: string) =>
          onInputChange(newValue, reason)
        }
        placeholder={t("searchOtherTalents")}
      />
      <Box minHeight="60px" position="relative">
        <OverlaySpinner isLoading={isLoading} sx={{ position: "absolute" }} />
        {showSearchResults && (
          <>
            {!!searchResults.length && (
              <Typography
                padding="12px 0 0 12px"
                variant="header"
                color="primary.main"
                textTransform="uppercase"
                fontWeight={700}
                fontSize="14px"
              >
                {t("searchResults")}
              </Typography>
            )}
            <List sx={{ maxHeight: "400px", overflow: "auto" }}>
              {searchResults.map((talent, index) => (
                <ListItem
                  key={talent.talentId}
                  dense
                  disablePadding
                  sx={{
                    height: "30px",
                    bgcolor: index % 2 ? "#fff" : "#FAFAFB",
                  }}
                >
                  <ListItemButton
                    onClick={() => handleSelect(talent)}
                    dense
                    sx={{ height: "30px", paddingLeft: "20px" }}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={
                          !!newSelectedTalents.find(
                            (newSelectedTalents) =>
                              newSelectedTalents.talentId === talent.talentId,
                          )
                        }
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <TalentStatusDisplayName
                      talentId={talent.talentId}
                      fullName={getName(talent)}
                      status={talent.status}
                      statusChanged={talent.statusChanged}
                      exclusivityEntryIds={talent.exclusivityEntryIds}
                    />
                    {talent.hasOverlappingAssignment && (
                      <InfoTooltip
                        style={{ width: "20px", height: "20px" }}
                        variant="warning"
                        title={t("talentHasOverlappingAssignment")}
                      />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
        {showNoSearchResults && (
          <Typography lineHeight="60px">
            {t("common:noSearchResults")}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SearchForOtherTalentsContent;
